import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';

import { useAuth } from '@/state/auth';

export interface WebSocketMessage {
  type: string;
  tenant: string;
  key: string;
  eventName: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  item?: Record<string, any>;
}

interface WebSocketState {
  isConnected: boolean;
  send: (message: object) => void;
  subscribe: (handler: MessageHandler) => () => void;
}

export interface WebSocketOptions {
  /** Connect to the per-user composite partition (default true) */
  user?: boolean;
}

type MessageHandler = (message: WebSocketMessage) => void;

const WS_URL = import.meta.env.VITE_WS_URL || '';
const RECONNECT_DELAYS = [1000, 2000, 4000, 8000, 16000, 30000];
const HEARTBEAT_INTERVAL = 5 * 60 * 1000; // 5 minutes

// --- Per-connection instance (external mutable state, not React state) ---

interface WSInstance {
  ws: WebSocket | null;
  handlers: Set<MessageHandler>;
  reconnectAttempt: number;
  reconnectTimer: ReturnType<typeof setTimeout> | null;
  heartbeatTimer: ReturnType<typeof setInterval> | null;
  connectionCount: number;
  isConnected: boolean;
  connectionListeners: Set<() => void>;
}

function createInstance(): WSInstance {
  return {
    ws: null,
    handlers: new Set(),
    reconnectAttempt: 0,
    reconnectTimer: null,
    heartbeatTimer: null,
    connectionCount: 0,
    isConnected: false,
    connectionListeners: new Set(),
  };
}

const instances = new Map<string, WSInstance>();

function getInstance(key: string): WSInstance {
  let inst = instances.get(key);
  if (!inst) {
    inst = createInstance();
    instances.set(key, inst);
  }
  return inst;
}

function notifyConnectionState(inst: WSInstance, nextState: boolean) {
  if (inst.isConnected === nextState) return;
  inst.isConnected = nextState;
  inst.connectionListeners.forEach(listener => listener());
}

function startHeartbeat(inst: WSInstance) {
  stopHeartbeat(inst);
  inst.heartbeatTimer = setInterval(() => {
    if (inst.ws?.readyState === WebSocket.OPEN) {
      inst.ws.send(JSON.stringify({ action: 'ping' }));
    }
  }, HEARTBEAT_INTERVAL);
}

function stopHeartbeat(inst: WSInstance) {
  if (inst.heartbeatTimer) {
    clearInterval(inst.heartbeatTimer);
    inst.heartbeatTimer = null;
  }
}

// Imperative connect/reconnect operating on an instance directly.
// These are NOT hooks — they mutate the external singleton state.

function scheduleReconnect(
  inst: WSInstance,
  getToken: () => Promise<string>,
  friend: string,
  userPartition: boolean
) {
  if (inst.reconnectTimer) return;
  const delay =
    RECONNECT_DELAYS[
      Math.min(inst.reconnectAttempt, RECONNECT_DELAYS.length - 1)
    ];
  inst.reconnectAttempt++;
  inst.reconnectTimer = setTimeout(() => {
    inst.reconnectTimer = null;
    connectInstance(inst, getToken, friend, userPartition);
  }, delay);
}

async function connectInstance(
  inst: WSInstance,
  getToken: () => Promise<string>,
  friend: string,
  userPartition: boolean
) {
  if (
    !WS_URL ||
    inst.ws?.readyState === WebSocket.OPEN ||
    inst.ws?.readyState === WebSocket.CONNECTING
  ) {
    return;
  }

  try {
    const token = await getToken();
    if (!token) return;

    const url = new URL(WS_URL);
    url.searchParams.set('token', token);
    if (userPartition) {
      url.searchParams.set('user', 'true');
    }
    if (friend) {
      url.searchParams.set('account', friend);
    }

    const socket = new WebSocket(url.toString());
    inst.ws = socket;

    socket.onopen = () => {
      inst.reconnectAttempt = 0;
      notifyConnectionState(inst, true);
      startHeartbeat(inst);
    };

    socket.onmessage = event => {
      try {
        const message = JSON.parse(event.data) as WebSocketMessage;
        inst.handlers.forEach(handler => handler(message));
      } catch {
        // Ignore non-JSON messages (e.g., pong responses)
      }
    };

    socket.onclose = () => {
      notifyConnectionState(inst, false);
      stopHeartbeat(inst);
      inst.ws = null;
      scheduleReconnect(inst, getToken, friend, userPartition);
    };

    socket.onerror = () => {
      // onclose will fire after onerror
    };
  } catch {
    scheduleReconnect(inst, getToken, friend, userPartition);
  }
}

function closeInstance(inst: WSInstance) {
  if (inst.reconnectTimer) {
    clearTimeout(inst.reconnectTimer);
    inst.reconnectTimer = null;
  }
  stopHeartbeat(inst);
  if (inst.ws) {
    inst.ws.onclose = null;
    inst.ws.close();
    inst.ws = null;
  }
  notifyConnectionState(inst, false);
}

// --- Hook ---

/**
 * Shared WebSocket hook. Connections are pooled by partition type:
 * - `{ user: true }` (default) → composite `{account}:{user}` partition
 * - `{ user: false }` → plain `{account}` partition
 *
 * Multiple hooks with the same options share one connection.
 */
export function useWebSocket(options?: WebSocketOptions): WebSocketState {
  const userPartition = options?.user ?? true;
  const instanceKey = userPartition ? 'user' : 'account';
  const inst = getInstance(instanceKey);

  const { getToken, friend } = useAuth();
  const getTokenRef = useRef(getToken);
  const friendRef = useRef(friend);
  const instRef = useRef(inst);

  useEffect(() => {
    getTokenRef.current = getToken;
    friendRef.current = friend;
    instRef.current = inst;
  });

  const isConnected = useSyncExternalStore(
    useCallback(
      (listener: () => void) => {
        inst.connectionListeners.add(listener);
        return () => {
          inst.connectionListeners.delete(listener);
        };
      },
      [inst]
    ),
    useCallback(() => inst.isConnected, [inst])
  );

  // Lifecycle: connect when first consumer mounts, close when last unmounts.
  useEffect(() => {
    const i = instRef.current;
    i.connectionCount++;
    if (i.connectionCount === 1 && WS_URL) {
      connectInstance(i, getTokenRef.current, friendRef.current, userPartition);
    }

    return () => {
      i.connectionCount--;
      if (i.connectionCount === 0) {
        closeInstance(i);
      }
    };
  }, [userPartition]);

  // Reconnect when the impersonated account changes.
  useEffect(() => {
    const i = instRef.current;
    if (i.ws && WS_URL) {
      notifyConnectionState(i, false);
      i.ws.onclose = null;
      i.ws.close();
      i.ws = null;
      i.reconnectAttempt = 0;
      connectInstance(i, getTokenRef.current, friendRef.current, userPartition);
    }
  }, [friend, userPartition]);

  const send = useCallback((message: object) => {
    const i = instRef.current;
    if (i.ws?.readyState === WebSocket.OPEN) {
      i.ws.send(JSON.stringify(message));
    }
  }, []);

  const subscribe = useCallback((handler: MessageHandler) => {
    const i = instRef.current;
    i.handlers.add(handler);
    return () => {
      i.handlers.delete(handler);
    };
  }, []);

  return { isConnected, send, subscribe };
}
